import { useSearchParams } from 'react-router-dom';
import { useActivePriorStudiesValue } from './useActivePriorStudies';

export const PRIOR_STUDIES_PARAM = 'prior_studies';
export const EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM = 'skip_studies';

/**
 * Read the raw `prior_studies` URL search param.
 *
 * @deprecated Most callers should read from `useActivePriorStudiesValue` instead;
 * the atom is the source of truth and the URL is a projection. This hook
 * remains for code paths that read the URL outside of the atom's case scope
 * (e.g., one-off URL construction during initial route resolution).
 */
export const usePriorStudiesParams = (): Nullable<string> => {
  const [searchParams] = useSearchParams();
  return searchParams.get(PRIOR_STUDIES_PARAM);
};

/**
 * Read the raw `skip_studies` URL search param.
 *
 * @deprecated See `usePriorStudiesParams`.
 */
export const useExcludedAutoloadedPriorParams = (): Nullable<string> => {
  const [searchParams] = useSearchParams();
  return searchParams.get(EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM);
};

const getPriorsQueryParametersSuffix = (priorStudiesList?: string | null): string => {
  return priorStudiesList != null && priorStudiesList.length > 0
    ? `${PRIOR_STUDIES_PARAM}=${priorStudiesList}`
    : '';
};

const getExcludedAutoloadedPriorsQueryParametersSuffix = (
  excludedAutoloadedPriorsList?: string | null
): string => {
  return excludedAutoloadedPriorsList != null && excludedAutoloadedPriorsList.length > 0
    ? `${EXCLUDED_AUTOLOADED_PRIOR_STUDIES_PARAM}=${excludedAutoloadedPriorsList}`
    : '';
};

export const getCaseStudyParamsSuffix = (
  priorStudiesList?: string | null,
  excludedAutoloadedPriorsList?: string | null
): string => {
  const params = [
    getPriorsQueryParametersSuffix(priorStudiesList),
    getExcludedAutoloadedPriorsQueryParametersSuffix(excludedAutoloadedPriorsList),
  ]
    .filter((suffix) => suffix.length > 0)
    .join('&');

  return params.length > 0 ? `?${params}` : '';
};

/**
 * Build a `?prior_studies=...&skip_studies=...` suffix for the given case
 * from the active-priors atom. Returns `''` when there are no active priors.
 */
export const useCaseStudyParamsSuffix = (caseSmid: Nullable<string>): string => {
  const { priors, excluded } = useActivePriorStudiesValue(caseSmid);
  return getCaseStudyParamsSuffix(priors.join(','), excluded.join(','));
};
